import { z } from "zod";
import { canonicalResultValueSchema } from "./canonical-result.js";
import { canonicalTwinBoundaryJson } from "./twin-boundary-json.js";
import { runAnswerEvidenceReferenceSchema, runAnswerEvidenceTextSha256 } from "./run-answer-evidence.js";
const identifier = z.string().min(1).max(200);
const field = z.string().min(1).max(240);
const text = z.string().min(1).max(8_000);
const precision = z.number().int().min(0).max(100);
const source = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/u);
const location = z.object({
    rowIndex: z.number().int().min(0).max(199), column: field,
    jsonPointer: runAnswerEvidenceReferenceSchema.shape.jsonPointer,
}).strict();
export const twinAnswerCellReferenceSchema = location.extend({ source }).strict();
const calculation = z.object({
    kind: z.literal("calculation"), operation: z.enum(["add", "subtract", "multiply", "divide"]),
    operands: z.array(twinAnswerCellReferenceSchema).min(2).max(32),
    fractionDigits: precision, rounding: z.literal("half_away_from_zero"),
}).strict().refine(value => value.operation === "add" || value.operands.length === 2, "Only addition accepts more than two operands.");
const expression = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cell"), reference: twinAnswerCellReferenceSchema,
        fractionDigits: precision.optional() }).strict(),
    calculation,
]);
const column = z.object({ field, label: field, fractionDigits: precision.optional() }).strict();
export const TWIN_ANSWER_DRAFT_SCHEMA_VERSION = "mlx.twin-answer-draft.v1";
const draftBody = {
    sources: z.array(z.object({ id: source, toolCallId: identifier }).strict()).min(1).max(32),
    blocks: z.array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("paragraph"), text }).strict(),
        z.object({ kind: z.literal("fact"), label: field, expression }).strict(),
        z.object({ kind: z.literal("table"), title: field, source,
            // Omit for a flat SQL result. A JSON location must select an array of records.
            jsonRows: location.optional(), columns: z.array(column).min(1).max(32),
            startRow: z.number().int().min(0).max(9_999), rowCount: z.number().int().min(1).max(200),
        }).strict(),
    ])).min(1)
        // Gemini ANY rejects this nested union with maxItems: 64.
        // Keep the bound in server validation and describe it in the tool schema.
        .refine(blocks => blocks.length <= 64, "An answer may contain at most 64 blocks.")
        .describe("At most 64 blocks; the server rejects larger answers."),
};
const checkDraftBody = (draft, context) => {
    if (new Set(draft.sources.map(item => item.id)).size !== draft.sources.length ||
        new Set(draft.sources.map(item => item.toolCallId)).size !== draft.sources.length) {
        context.addIssue({ code: "custom", message: "Sources must have unique IDs and tool calls." });
    }
    if (!draft.blocks.some(block => block.kind !== "paragraph")) {
        context.addIssue({ code: "custom", message: "An answer document must reference source values." });
    }
    if (JSON.stringify(draft).length > 64_000) {
        context.addIssue({ code: "custom", message: "Answer proposal exceeds its size limit." });
    }
};
/**
 * Model proposal: values and table rows are supplied by the server, never copied by the model.
 * Protocol constants are server-owned: the runtime adds the draft version before materializing.
 */
export const twinAnswerDocumentProposalSchema = z.object(draftBody).strict().superRefine(checkDraftBody);
/** A versioned proposal, as materialized and stored with the run. */
export const twinAnswerDocumentDraftSchema = z.object({
    schemaVersion: z.literal(TWIN_ANSWER_DRAFT_SCHEMA_VERSION), ...draftBody,
}).strict().superRefine(checkDraftBody);
/** AgentTool parameters are JSON Schema, not a Zod validator instance. */
export const twinAnswerDocumentToolSchema = z.toJSONSchema(twinAnswerDocumentProposalSchema, {
    target: "draft-7", unrepresentable: "throw", io: "input",
});
export const twinAnswerValueSchema = z.object({
    value: canonicalResultValueSchema, display: z.string().max(16_000),
    references: z.array(twinAnswerCellReferenceSchema).min(1).max(32),
    calculation: calculation.optional(),
}).strict();
export const twinAnswerDocumentSchema = z.object({
    schemaVersion: z.literal("mlx.twin-answer-document.v1"), draft: twinAnswerDocumentDraftSchema,
    verificationScope: z.literal("source_values_and_declared_arithmetic"),
    blocks: z.array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("paragraph"), text, verified: z.literal(false) }).strict(),
        z.object({ kind: z.literal("fact"), label: field, value: twinAnswerValueSchema }).strict(),
        z.object({ kind: z.literal("table"), title: field, columns: z.array(column).min(1).max(32),
            rows: z.array(z.array(twinAnswerValueSchema).max(32)).max(200),
            availableRows: z.number().int().nonnegative(), startRow: z.number().int().nonnegative(),
            sourceComplete: z.boolean(), completenessCaveat: z.string().max(500).optional(),
        }).strict(),
    ])).min(1).max(64),
    sourceCaveats: z.array(z.object({ source, text: z.string().max(500) }).strict()).max(32),
}).strict();
/** Server-owned receipt; renderers and evaluators bind this exact document. */
export const runAnswerDocumentEventSchema = z.object({
    schemaVersion: z.literal("mlx.run-answer-document.v1"),
    runId: identifier, attemptNumber: z.number().int().positive(),
    document: twinAnswerDocumentSchema,
    documentSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    answerSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    sources: z.array(z.object({ id: source, toolCallId: identifier,
        certificateId: identifier,
        certificateSha256: z.string().regex(/^[a-f0-9]{64}$/u),
        canonicalResultSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    }).strict()).min(1).max(32),
}).strict();
export function twinAnswerDocumentSha256(document) {
    return runAnswerEvidenceTextSha256(canonicalTwinBoundaryJson(twinAnswerDocumentSchema.parse(document)));
}
